// Image Tiles implementation
export default class ImageTiles {
  constructor() {
    this.init();
  }

  init() {
    // Wait for the DOM to be fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Get the hero grid container
    this.heroGrid = document.querySelector('.hero-grid');
    if (!this.heroGrid) return;

    // Get all hero cells (the ones we've added with the hero-cell class)
    this.heroCells = document.querySelectorAll('.hero-cell');
    if (this.heroCells.length === 0) return;

    // Get image tiles
    this.imageTiles = document.querySelectorAll('.image-tile');
    if (this.imageTiles.length === 0) return;

    // Keep track of the currently active cell
    this.activeCell = null;
    
    // Track which images are currently displayed
    this.activeImages = [];

    // Define fixed mappings for which cells trigger which image positions
    this.cellMappings = {
      // Mobile and shared cells
      'a1': ['e3', 'c5', 'g3'],
      'a3': ['e1', 'c5', 'g5'],
      'a5': ['e3', 'c1', 'g3'],
      'b1': ['e3', 'e5', 'g3'],
      'b5': ['c1', 'e3', 'g1'],
      'c1': ['a5', 'e3', 'g5'],
      'c5': ['a1', 'e3', 'g1'],
      'e1': ['a3', 'c5', 'g3'],
      'e3': ['a1', 'c1', 'g5'],
      'e5': ['a3', 'c1', 'g3'],
      'g1': ['a5', 'c5', 'e3'],
      'g3': ['a1', 'c1', 'e5'],
      'g5': ['a3', 'c5', 'e1'],
      
      // Medium screen specific cells
      'b5-md': ['c9', 'e7', 'g9'],
      'c5-md': ['b9', 'e7', 'g9'],
      'e7': ['b9', 'c9', 'g9'],
      'b9': ['c5-md', 'e7', 'g9'],
      'c9': ['b5-md', 'e7', 'g9'],
      'e9': ['b9', 'c9', 'g9'],
      'g9': ['b9', 'c9', 'e7']
    };

    // Initialize the hover effect
    this.initHoverEffect();
  }

  initHoverEffect() {
    // Add hover event listeners to hero cells
    this.heroCells.forEach(cell => {
      const cellId = cell.getAttribute('data-cell-id');
      
      // Skip cells that don't have a mapping
      if (!cellId || !this.cellMappings[cellId]) return;
      
      // Add event listeners
      cell.addEventListener('mouseenter', () => {
        // Clear any pending hide timeouts
        if (this.hideTimeout) {
          clearTimeout(this.hideTimeout);
          this.hideTimeout = null;
        }
        
        this.showImagesForCell(cell);
      });
      
      cell.addEventListener('mouseleave', () => {
        // Use a timeout to prevent immediate hiding
        // This helps with flickering when moving between cells
        this.hideTimeout = setTimeout(() => {
          if (this.activeCell === cell) {
            this.hideImagesForCell(cell);
          }
        }, 50); // Small delay to prevent flickering
      });
      
      // Make sure the cell has a cursor pointer to indicate it's interactive
      cell.style.cursor = 'pointer';
    });
  }

  showImagesForCell(cell) {
    // If we already have active images for this cell, don't do anything
    if (this.activeCell === cell) return;
    
    // Hide any previously visible images
    this.hideAllImages();
    
    // Set this cell as the active cell
    this.activeCell = cell;
    
    // Get the cell ID
    const cellId = cell.getAttribute('data-cell-id');
    if (!cellId || !this.cellMappings[cellId]) return;
    
    // Get the target cell IDs for this trigger cell
    const targetCellIds = this.cellMappings[cellId];
    
    // Get three random images
    const randomImages = this.getRandomImages(targetCellIds.length);
    
    // Clear the active images array
    this.activeImages = [];
    
    // Show the images in the target cells
    targetCellIds.forEach((targetCellId, index) => {
      if (index < randomImages.length) {
        const targetCell = document.querySelector(`.hero-cell[data-cell-id="${targetCellId}"]`);
        if (targetCell) {
          this.placeImageInCell(randomImages[index], targetCell);
          this.activeImages.push(randomImages[index]);
        }
      }
    });
  }

  hideImagesForCell(cell) {
    // Only hide images if this is the active cell
    if (this.activeCell === cell) {
      this.hideAllImages();
      this.activeCell = null;
    }
  }

  getRandomImages(count) {
    // Shuffle the images and pick the first 'count' images
    const shuffled = Array.from(this.imageTiles).sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }

  placeImageInCell(image, cell) {
    // Get the position and dimensions of the cell
    const cellRect = cell.getBoundingClientRect();
    const heroRect = this.heroGrid.getBoundingClientRect();
    
    // Position the image tile relative to the hero section
    const left = cellRect.left - heroRect.left;
    const top = cellRect.top - heroRect.top;
    
    // Set the image position and size exactly matching the cell
    image.style.left = `${left}px`;
    image.style.top = `${top}px`;
    image.style.width = `${cellRect.width}px`;
    image.style.height = `${cellRect.height}px`;
    
    // Show the image
    image.classList.remove('hidden');
    
    // Use setTimeout to ensure the transition works
    setTimeout(() => {
      image.classList.add('is-visible');
    }, 10);
  }

  hideAllImages() {
    // Hide only the active images
    if (this.activeImages.length > 0) {
      this.activeImages.forEach(image => {
        image.classList.remove('is-visible');
        
        // Hide the image after the transition completes
        setTimeout(() => {
          image.classList.add('hidden');
        }, 600); // Match the transition duration from CSS
      });
      
      // Clear the active images array
      this.activeImages = [];
    }
  }
}

// Initialize the image tiles when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ImageTiles();
});

// Also initialize on turbo:load for Bridgetown's Turbo support
document.addEventListener('turbo:load', () => {
  new ImageTiles();
});

// Fallback for browsers that might not fire DOMContentLoaded properly
window.addEventListener('load', () => {
  if (!window.imageTilesInitialized) {
    window.imageTilesInitialized = true;
    new ImageTiles();
  }
});
